import {
  Box,
  Divider,
  SkeletonLine,
  TableToolbar,
  SkeletonGroup,
} from '@wix/design-system';
import React, {
  cloneElement,
  FunctionComponent,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { CollectionSearchProps } from '../CollectionSearch';
import { CollectionProvider } from '../CollectionContext';
import { FiltersMap } from '@wix/bex-core';
import { ToolbarCollectionState } from '../../state';
import { observer } from 'mobx-react-lite';
import { PartialFunctionComponent } from '../PartialFunctionComponent';
import type { MultiLevelSortingElement } from '../assertComponentType';
import { ToolbarItemBox } from '../ToolbarItemBox';
import { SearchOrCustomFilter } from './SearchOrCustomFilter';
import { classes, st } from './CollectionToolbar.st.css.js';
import { ToolbarTitle } from '../ToolbarTitle/ToolbarTitle';
import { InitialLoadingConditional } from '../InitialLoadingConditional';
import { assertComponentType } from '../assertComponentType';
import {
  ToolbarBaseProps,
  ToolbarCollectionBaseProps,
} from '../ToolbarCollection';

export interface CollectionToolbarProps<T, F extends FiltersMap> {
  state: ToolbarCollectionState<T, F>;
  title?: ToolbarCollectionBaseProps<T, F>['title'];
  subtitle?: ToolbarCollectionBaseProps<T, F>['subtitle'];
  tabs?: ToolbarCollectionBaseProps<T, F>['tabs'];
  customColumns?: ToolbarCollectionBaseProps<T, F>['customColumns'];
  multiLevelSorting?: MultiLevelSortingElement;
  views?: ToolbarCollectionBaseProps<T, F>['views'];
  search?: ToolbarBaseProps<T>['search'];
  searchMaxLength?: CollectionSearchProps<T, F>['maxLength'];
  onSearchChange?: CollectionSearchProps<T, F>['onSearchChange'];
  onSearchClick?: CollectionSearchProps<T, F>['onSearchClick'];
  onSearchBlur?: CollectionSearchProps<T, F>['onSearchBlur'];
  showTotal?: ToolbarCollectionBaseProps<T, F>['showTotal'];
  dataHook?: string;
  filters?: ToolbarCollectionBaseProps<T, F>['filters'];
  tableGridSwitchButton?: ToolbarCollectionBaseProps<
    T,
    F
  >['tableGridSwitchButton'];
  exportButton?: ToolbarCollectionBaseProps<T, F>['exportButton'];
  addNewButton?: ToolbarCollectionBaseProps<T, F>['addNewButton'];
  primaryActionButton?: ToolbarCollectionBaseProps<T, F>['primaryActionButton'];
  dragAndDropReorderModeToolbar?: ToolbarCollectionBaseProps<
    T,
    F
  >['dragAndDropReorderModeToolbar'];
}

function _CollectionToolbar<T, F extends FiltersMap>(
  props: CollectionToolbarProps<T, F>,
) {
  const {
    state,
    search = true,
    title,
    subtitle,
    tabs,
    customColumns,
    multiLevelSorting,
    views,
    dataHook,
    showTotal,
    filters: filtersProp,
    onSearchChange,
    onSearchClick,
    onSearchBlur,
    searchMaxLength,
    tableGridSwitchButton,
    exportButton,
    dragAndDropReorderModeToolbar,
  } = props;
  assertComponentType(
    props.primaryActionButton,
    'PrimaryActionButton',
    'PrimaryActionButton',
  );
  const primaryActionButton = props.primaryActionButton ?? props.addNewButton;

  const { collection, featuredComponents } = state;

  const MoreFiltersButton = featuredComponents.moreFiltersButton
    .value as PartialFunctionComponent as FunctionComponent | null;

  const viewsToolbarItem = views && (
    <ToolbarItemBox dataHook="collection-table-toolbar-views-item">
      {views}
    </ToolbarItemBox>
  );

  const TagsFiltersWrapper =
    state.tagsCollectionState?.components.CollectionToolbarFilters;

  const filtersElement = useMemo(() => {
    return filtersProp ?? (TagsFiltersWrapper && <TagsFiltersWrapper />);
  }, [filtersProp]);

  let left: JSX.Element | undefined;

  if (tabs) {
    left = (
      <ToolbarItemBox removeStartPadding expandable layout="button">
        {tabs}
      </ToolbarItemBox>
    );
  } else if (title) {
    if (React.isValidElement(title) && title.type === ToolbarTitle) {
      left = title;
    } else {
      left = (
        <ToolbarTitle showTotal={showTotal} title={title} subtitle={subtitle} />
      );
    }
  }

  /* checking if something is rendered in the .left TableToolbar.ItemGroup (not empty) */
  const [rendersStart, setRendersStart] = useState<boolean>();

  useEffect(() => {
    setRendersStart(
      !!left ||
        !!viewsToolbarItem ||
        (state.toolbarFiltersState?.totalInlineFilters.value !== undefined &&
          state.toolbarFiltersState?.totalInlineFilters.value !== null &&
          state.toolbarFiltersState?.totalInlineFilters.value > 0),
    );
  }, [left, viewsToolbarItem, state.toolbarFiltersStateInitialized]);

  const shouldAddDivider =
    tableGridSwitchButton && (search || MoreFiltersButton);

  const analyticsCustomColumnsDiscoverability =
    state.container.experiments?.get(
      'specs.analytics.CustomColumnsWithLabelAndNewIcon',
    );

  const showCustomColumnsOnTheLeft =
    analyticsCustomColumnsDiscoverability === 'b' ||
    analyticsCustomColumnsDiscoverability === 'c';

  const tableSkeletonEnabled = state.container.internalExperiments?.enabled(
    'specs.cairo.TableSkeletonLoader',
  );

  return (
    <CollectionProvider value={collection}>
      <Box direction="vertical" dataHook={dataHook}>
        <Box minHeight={subtitle ? '78px' : '60px'}>
          {state.isReorderModeToolbarVisible ? (
            dragAndDropReorderModeToolbar
          ) : (
            <TableToolbar
              removeVerticalPadding
              className={st(classes.root)}
              dataHook="collection-table-toolbar"
            >
              <InitialLoadingConditional
                state={state}
                fallback={
                  tableSkeletonEnabled ? (
                    <TableToolbar.ItemGroup
                      dataHook="toolbar-skeleton"
                      position="start"
                    >
                      <SkeletonGroup>
                        <SkeletonLine width="169px" />
                      </SkeletonGroup>
                    </TableToolbar.ItemGroup>
                  ) : null
                }
              >
                <TableToolbar.ItemGroup
                  position="start"
                  className={st(classes.left, { expandable: !!tabs })}
                  dataHook="collection-table-toolbar-filters"
                >
                  {left ?? (
                    <>
                      {viewsToolbarItem}
                      {filtersElement}
                    </>
                  )}
                </TableToolbar.ItemGroup>

                <TableToolbar.ItemGroup
                  position="end"
                  className={st(
                    classes.right,
                    {},
                    ...[rendersStart ? undefined : classes.fullWidth],
                  )}
                >
                  {left ? (
                    <>
                      {viewsToolbarItem}
                      {filtersElement}
                    </>
                  ) : undefined}
                  {MoreFiltersButton && (
                    <TableToolbar.Item>
                      <MoreFiltersButton />
                    </TableToolbar.Item>
                  )}
                  {customColumns && showCustomColumnsOnTheLeft && (
                    <TableToolbar.Item>{customColumns}</TableToolbar.Item>
                  )}
                  {multiLevelSorting && (
                    <TableToolbar.Item>{multiLevelSorting}</TableToolbar.Item>
                  )}
                  {exportButton && (
                    <TableToolbar.Item>
                      {cloneElement(exportButton, {
                        toolbarType: 'default',
                      })}
                    </TableToolbar.Item>
                  )}
                  {customColumns && !showCustomColumnsOnTheLeft && (
                    <TableToolbar.Item>{customColumns}</TableToolbar.Item>
                  )}
                  {search && (
                    <SearchOrCustomFilter
                      state={collection}
                      searchMaxLength={searchMaxLength}
                      onSearchChange={onSearchChange}
                      onSearchClick={onSearchClick}
                      onSearchBlur={onSearchBlur}
                      search={search}
                    />
                  )}
                  {primaryActionButton && (
                    <TableToolbar.Item>{primaryActionButton}</TableToolbar.Item>
                  )}
                  {shouldAddDivider && (
                    <Box alignSelf="stretch" paddingLeft="SP2">
                      <Divider
                        direction="vertical"
                        dataHook="collection-table--grid-switch--divider"
                      />
                    </Box>
                  )}
                  {tableGridSwitchButton && (
                    <TableToolbar.Item>
                      {tableGridSwitchButton}
                    </TableToolbar.Item>
                  )}
                </TableToolbar.ItemGroup>
              </InitialLoadingConditional>
            </TableToolbar>
          )}
        </Box>
      </Box>
    </CollectionProvider>
  );
}

export const CollectionToolbar = observer(_CollectionToolbar);
